import { useEffect, useMemo } from "react";
import { useTolgee } from "@tolgee/react";
import { useSelector } from "react-redux";
import { useShallow } from "zustand/react/shallow";
import {
  ENGLISH_LANG_CODE,
  NotificationType,
  Roles,
} from "@evennt-tech/api-v2-core";
import { apiV2 } from "api/firebase";
import { selectUser } from "store/auth/selectors";
import useViewMode, { ViewMode } from "store/viewMode/viewMode.store";
import { useCurrentExhibitor } from "store/current-exhibitor/current-exhibitor.store";
import { useExhibitorAvailableLanguagesStore } from "store/localization/exhibitor-available-languages.store";
import {
  stagedLanguageStore,
  useStagedLanguageStore,
} from "store/localization/staged-language.store";
import { resolveActiveLanguage } from "tools/localization/resolveActiveLanguage";
import { usePublishedLanguages } from "./usePublishedLanguages";
import { useEffectiveShowLanguages } from "./useEffectiveShowLanguages";
import useCurrentShowId from "./useCurrentShowId";

// Mounted once, by the Layout, so a fixed listener id cannot be dropped by a
// sibling instance unmounting.
const OFFERED_LANGUAGES_LISTENER_ID = "apply_exhibitor_language";

// Global events: an Admin enabling or disabling a language grant on any Show.
const OFFERED_LANGUAGES_REFETCH_ON: NotificationType[] = [
  NotificationType.PAID_FEATURES_CREATED,
  NotificationType.PAID_FEATURES_DELETED,
];

export const useApplyExhibitorLanguage = () => {
  const tolgee = useTolgee();
  const showId = useCurrentShowId();
  const { role } = useSelector(selectUser);
  const { mode } = useViewMode();
  const {
    codes: availableCodes,
    isLoading: isPublishedLoading,
  } = usePublishedLanguages();
  const {
    selectedLanguage,
    hasExhibitor,
    isExhibitorLoading,
    setSelectedLanguage,
  } = useCurrentExhibitor(
    useShallow((state) => ({
      selectedLanguage: state.item?.user?.selectedLanguage,
      hasExhibitor: !!state.item,
      isExhibitorLoading: state.isLoading,
      setSelectedLanguage: state.setSelectedLanguage,
    }))
  );
  const stagedLanguage = useStagedLanguageStore((state) => state.code);
  const {
    effectiveCodes,
    adminDisabledCodes,
    defaultLanguage,
    isLoading: isShowLanguagesLoading,
  } = useEffectiveShowLanguages(showId);

  const isExhibitor = role === Roles.EXHIBITOR || mode === ViewMode.EXHIBITOR;
  const isExhibitorUser = role === Roles.EXHIBITOR;

  const {
    offeredCodes,
    hasFetchedOffered,
    fetchOffered,
  } = useExhibitorAvailableLanguagesStore((state) => ({
    offeredCodes: state.languageCodes,
    hasFetchedOffered: state.hasFetched,
    fetchOffered: state.fetch,
  }));

  useEffect(() => {
    if (!isExhibitorUser) return undefined;

    fetchOffered();

    OFFERED_LANGUAGES_REFETCH_ON.forEach((key) => {
      apiV2.notification.subscribe({
        key,
        listenerId: OFFERED_LANGUAGES_LISTENER_ID,
        callback: async () => {
          await fetchOffered(true);
        },
      });
    });

    return () => {
      OFFERED_LANGUAGES_REFETCH_ON.forEach((key) =>
        apiV2.notification.unsubscribe({
          key,
          listenerId: OFFERED_LANGUAGES_LISTENER_ID,
        })
      );
    };
  }, [isExhibitorUser, fetchOffered]);

  // Outside a Show only the languages one of the exhibitor's Shows offers are
  // usable, so a language the Admin switched off everywhere falls back too.
  const outsideShowCodes = useMemo(
    () =>
      availableCodes.filter(
        (code) => code === ENGLISH_LANG_CODE || offeredCodes.includes(code)
      ),
    [availableCodes, offeredCodes]
  );

  // A language picked before sign-in outranks the stored one. It is persisted
  // only when one of the exhibitor's Shows offers it, then dropped.
  useEffect(() => {
    if (!role || !stagedLanguage) return;

    const staged = stagedLanguageStore.getState();

    if (staged.persistState !== "idle") return;

    if (!isExhibitorUser) {
      staged.clear();
      return;
    }

    if (
      !hasExhibitor ||
      !hasFetchedOffered ||
      isPublishedLoading ||
      !availableCodes.length
    ) {
      return;
    }

    if (
      !outsideShowCodes.includes(stagedLanguage) ||
      stagedLanguage === selectedLanguage
    ) {
      staged.clear();
      return;
    }

    staged.markPending();

    setSelectedLanguage(stagedLanguage)
      .then(() => stagedLanguageStore.getState().clear())
      .catch((e) => {
        console.error("Failed to persist staged language: ", e);
        stagedLanguageStore.getState().markFailed();
      });
  }, [
    role,
    stagedLanguage,
    isExhibitorUser,
    hasExhibitor,
    hasFetchedOffered,
    isPublishedLoading,
    availableCodes,
    outsideShowCodes,
    selectedLanguage,
    setSelectedLanguage,
  ]);

  useEffect(() => {
    if (role && !isExhibitor) {
      if (tolgee.getLanguage() !== ENGLISH_LANG_CODE) {
        tolgee.changeLanguage(ENGLISH_LANG_CODE);
      }
      return;
    }

    if (!isExhibitor || isPublishedLoading || isExhibitorLoading) {
      return;
    }

    if (!availableCodes.length) {
      return;
    }

    if (showId && (isShowLanguagesLoading || !defaultLanguage)) {
      return;
    }

    if (!showId && isExhibitorUser && !hasFetchedOffered) {
      return;
    }

    // A staged code that would be rejected is skipped rather than applied and
    // then reverted once it is dropped.
    const isStagedUsable =
      isExhibitorUser &&
      !!stagedLanguage &&
      (showId
        ? stagedLanguage === ENGLISH_LANG_CODE ||
          effectiveCodes.includes(stagedLanguage)
        : outsideShowCodes.includes(stagedLanguage));

    tolgee.changeLanguage(
      resolveActiveLanguage({
        preferred:
          (isStagedUsable ? stagedLanguage : null) ??
          selectedLanguage ??
          tolgee.getLanguage(),
        availableCodes:
          !showId && isExhibitorUser ? outsideShowCodes : availableCodes,
        ...(showId
          ? {
              effectiveCodes,
              adminDisabledCodes,
              showDefaultLanguage: defaultLanguage,
            }
          : {}),
      })
    );
  }, [
    role,
    isExhibitor,
    showId,
    availableCodes,
    stagedLanguage,
    selectedLanguage,
    isExhibitorLoading,
    isPublishedLoading,
    isShowLanguagesLoading,
    effectiveCodes,
    adminDisabledCodes,
    defaultLanguage,
    isExhibitorUser,
    hasFetchedOffered,
    outsideShowCodes,
    tolgee,
  ]);
};
